import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil, FileUp, Eye, Download, HelpCircle } from 'lucide-react';
import { Button, Modal, DateInput, formatDateDisplay } from '../../common';
import MoneyDisplay from '../common/MoneyDisplay';
import StatusDot from '../common/StatusDot';
import ConfirmDelete from '../common/ConfirmDelete';
import FileDropArea from '../common/FileDropArea';
import ContractForm, { type ContractFormHandle } from './ContractForm';
import AddActivityForDocumentButton from '../common/AddActivityForDocumentButton';
import { SearchableSelect } from '../../common/SearchableSelect';
import {
  getContractDetail, updateContract,
  createGuarantee, updateGuarantee, deleteGuarantee, uploadGuaranteeFile,
  createInvoice, updateInvoice, deleteInvoice, uploadInvoiceFile,
  createReception, updateReception, deleteReception, uploadReceptionFile,
  createContractDocument, updateContractDocument, deleteContractDocument, deleteContractFile, uploadFileToDocument,
  createAddendum, updateAddendum, deleteAddendum, uploadAddendumFile
} from '../../../services/dossier.service';
import { downloadAttachmentFile } from '../../../services/attachments.service';
import { getContractTypes, type ContractType } from '../../../services/nomenclatoare.service';
import type { ContractDetail } from '../../../types';

interface Props {
  contract: any;
  dossierId: number;
  projectId?: number;
  onRefresh: () => void;
  onDelete: () => void;
}

const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';

export default function ContractCard({ contract, dossierId, projectId, onRefresh, onDelete }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detail, setDetail] = useState<ContractDetail | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const editFormRef = useRef<ContractFormHandle>(null);

  // Modal form states
  const [showDocModal, setShowDocModal] = useState(false);
  const [docForm, setDocForm] = useState({ document_name: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [docCategory, setDocCategory] = useState<'contract' | 'aditional'>('contract');

  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false);
  const [guaranteeForm, setGuaranteeForm] = useState({ guarantee_type: 'buna_executie', amount: '', issue_date: '', expiry_date: '', issuer: '' });
  const [guaranteeFiles, setGuaranteeFiles] = useState<File[]>([]);
  const [creatingGuarantee, setCreatingGuarantee] = useState(false);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: '', invoice_date: '', amount: '', vat_amount: '', total_amount: '' });
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  const [showReceptionModal, setShowReceptionModal] = useState(false);
  const [receptionForm, setReceptionForm] = useState({ reception_type: 'predare_primire', reception_date: '', reception_number: '', description: '' });
  const [receptionFiles, setReceptionFiles] = useState<File[]>([]);
  const [creatingReception, setCreatingReception] = useState(false);

  const [contractTypeLabels, setContractTypeLabels] = useState<Record<string, string>>({});
  const [pendingDelete, setPendingDelete] = useState<{ action: () => void; message: string } | null>(null);

  // Edit document state
  const [editDocId, setEditDocId] = useState<number | null>(null);
  const [editDocForm, setEditDocForm] = useState({ document_name: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [editDocFiles, setEditDocFiles] = useState<File[]>([]);
  const [savingEditDoc, setSavingEditDoc] = useState(false);

  // Acte adiționale (addendums) — entitatea care schimbă valoarea/durata contractului.
  // Model: un act adițional stabilește NOUA valoare totală a contractului; ultimul urcat determină valoarea curentă (amount_mode='absolute'). 'none' = fără efect asupra valorii (doar durată/clauze). 'delta' rămâne suportat doar pentru actele istorice deja introduse.
  const emptyAddendum = { addendum_number: '', addendum_date: '', amount_mode: 'absolute' as 'delta' | 'absolute' | 'none', value_change: '', new_end_date: '', description: '' };
  const [showAddendumModal, setShowAddendumModal] = useState(false);
  const [addendumForm, setAddendumForm] = useState({ ...emptyAddendum });
  const [addendumFiles, setAddendumFiles] = useState<File[]>([]);
  const [creatingAddendum, setCreatingAddendum] = useState(false);
  const [editAddendumId, setEditAddendumId] = useState<number | null>(null);
  const [editAddendumForm, setEditAddendumForm] = useState({ ...emptyAddendum });
  const [editAddendumFiles, setEditAddendumFiles] = useState<File[]>([]);
  const [savingEditAddendum, setSavingEditAddendum] = useState(false);

  // Edit guarantee state
  const [editGuaranteeId, setEditGuaranteeId] = useState<number | null>(null);
  const [editGuaranteeForm, setEditGuaranteeForm] = useState({ guarantee_type: 'buna_executie', amount: '', issue_date: '', expiry_date: '', issuer: '' });
  const [savingEditGuarantee, setSavingEditGuarantee] = useState(false);

  // Edit invoice state
  const [editInvoiceId, setEditInvoiceId] = useState<number | null>(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({ invoice_number: '', invoice_date: '', amount: '', vat_amount: '', total_amount: '' });
  const [savingEditInvoice, setSavingEditInvoice] = useState(false);

  // Edit reception state
  const [editReceptionId, setEditReceptionId] = useState<number | null>(null);
  const [editReceptionForm, setEditReceptionForm] = useState({ reception_type: 'predare_primire', reception_date: '', reception_number: '', description: '' });
  const [savingEditReception, setSavingEditReception] = useState(false);

  useEffect(() => {
    getContractTypes().then(types => {
      const labels: Record<string, string> = {};
      types.forEach(t => { labels[t.code] = t.name; });
      setContractTypeLabels(labels);
    }).catch(console.error);
  }, []);

  const loadDetail = async () => {
    try {
      const data = await getContractDetail(dossierId, contract.id);
      setDetail(data);
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut încărca detaliile contractului. Încearcă din nou.');
    }
  };

  useEffect(() => { if (isExpanded) loadDetail(); }, [isExpanded]);

  // Sursa valorilor financiare afișate: preferă detaliul contractului (reîncărcat sincron de
  // loadDetail() după fiecare add/delete de factură/garanție/act/document) în locul prop-ului din
  // lista părinte, care se actualizează cu întârziere prin onRefresh(). Astfel header-ul reflectă
  // imediat valorile LIVE. Backend-ul întoarce obiectul contract sub cheia `contract` din detail.
  const fin: any = (detail as any)?.contract ?? contract;
  const remaining = parseFloat(fin.remaining_value) || 0;
  const adjusted = parseFloat(fin.adjusted_value) || parseFloat(fin.contract_value) || 0;
  const invoiced = parseFloat(fin.invoiced_total) || 0;
  const addendumTotal = parseFloat(fin.addendum_total) || 0;

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  const handleAddDocument = async () => {
    if (!docForm.document_name) return;
    setCreatingDoc(true);
    try {
      const response: any = await createContractDocument(contract.id, {
        document_name: docForm.document_name,
        document_type: docCategory === 'contract' ? 'contract' : 'aditional',
        notes: docForm.notes || undefined,
        start_date: docForm.start_date || undefined,
        end_date: docForm.end_date || undefined,
        nr_registratura: docForm.nr_registratura || undefined,
        data_registratura: docForm.data_registratura || undefined,
        data_document: docForm.data_document || undefined,
        data_expirare: docForm.data_expirare || undefined,
      });
      const newDocId = response?.document?.id;
      if (docFiles.length > 0 && newDocId) {
        for (const file of docFiles) {
          await uploadFileToDocument(contract.id, newDocId, file);
        }
      }
      setShowDocModal(false);
      setDocForm({ document_name: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
      setDocFiles([]);
      await loadDetail();
      onRefresh();
      toast.success('Document adăugat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva documentul. Încearcă din nou.');
    }
    finally { setCreatingDoc(false); }
  };

  const buildAddendumPayload = (form: typeof emptyAddendum) => ({
    addendum_number: form.addendum_number || undefined,
    addendum_type: 'value',
    addendum_date: form.addendum_date || undefined,
    amount_mode: form.amount_mode,
    value_change: form.amount_mode === 'none' ? undefined : (form.value_change !== '' ? parseFloat(form.value_change) : 0),
    new_end_date: form.new_end_date || undefined,
    description: form.description || undefined,
  });

  const handleAddAddendum = async () => {
    setCreatingAddendum(true);
    try {
      const response: any = await createAddendum(contract.id, buildAddendumPayload(addendumForm));
      const newId = response?.addendum?.id;
      if (addendumFiles.length > 0 && newId) {
        await uploadAddendumFile(contract.id, newId, addendumFiles[0]);
      }
      setShowAddendumModal(false);
      setAddendumForm({ ...emptyAddendum });
      setAddendumFiles([]);
      await loadDetail();
      onRefresh();
      toast.success('Act adițional adăugat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva actul adițional. Încearcă din nou.');
    }
    finally { setCreatingAddendum(false); }
  };

  const startEditAddendum = (a: any) => {
    setEditAddendumId(a.id);
    setEditAddendumForm({
      addendum_number: a.addendum_number || '',
      addendum_date: toDateStr(a.addendum_date),
      amount_mode: (a.amount_mode as 'delta' | 'absolute' | 'none') || 'delta',
      value_change: a.value_change != null ? String(a.value_change) : '',
      new_end_date: toDateStr(a.new_end_date),
      description: a.description || '',
    });
    setEditAddendumFiles([]);
  };

  const handleSaveEditAddendum = async () => {
    if (!editAddendumId) return;
    setSavingEditAddendum(true);
    try {
      await updateAddendum(contract.id, editAddendumId, buildAddendumPayload(editAddendumForm));
      if (editAddendumFiles.length > 0) {
        await uploadAddendumFile(contract.id, editAddendumId, editAddendumFiles[0]);
      }
      setEditAddendumId(null);
      setEditAddendumFiles([]);
      await loadDetail(); onRefresh();
      toast.success('Act adițional salvat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva actul adițional. Încearcă din nou.');
    }
    finally { setSavingEditAddendum(false); }
  };

  const handleDeleteAddendum = async (id: number) => {
    try {
      await deleteAddendum(contract.id, id);
      await loadDetail(); onRefresh();
      toast.success('Act adițional șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge actul adițional. Încearcă din nou.');
    }
  };

  const handleAddGuarantee = async () => {
    setCreatingGuarantee(true);
    try {
      const response: any = await createGuarantee(contract.id, {
        ...guaranteeForm,
        amount: guaranteeForm.amount ? parseFloat(guaranteeForm.amount) : 0
      });
      const newId = response?.guarantee?.id;
      if (guaranteeFiles.length > 0 && newId) {
        for (const file of guaranteeFiles) {
          await uploadGuaranteeFile(contract.id, newId, file);
        }
      }
      setShowGuaranteeModal(false);
      setGuaranteeForm({ guarantee_type: 'buna_executie', amount: '', issue_date: '', expiry_date: '', issuer: '' });
      setGuaranteeFiles([]);
      await loadDetail();
      onRefresh();
      toast.success('Garanție adăugată.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva garanția. Încearcă din nou.');
    }
    finally { setCreatingGuarantee(false); }
  };

  const handleAddInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const amt = parseFloat(invoiceForm.amount) || 0;
      const vat = parseFloat(invoiceForm.vat_amount) || 0;
      const response: any = await createInvoice(contract.id, {
        ...invoiceForm,
        amount: amt,
        vat_amount: vat,
        total_amount: invoiceForm.total_amount ? parseFloat(invoiceForm.total_amount) : amt + vat
      });
      const newId = response?.invoice?.id;
      if (invoiceFiles.length > 0 && newId) {
        for (const file of invoiceFiles) {
          await uploadInvoiceFile(contract.id, newId, file);
        }
      }
      setShowInvoiceModal(false);
      setInvoiceForm({ invoice_number: '', invoice_date: '', amount: '', vat_amount: '', total_amount: '' });
      setInvoiceFiles([]);
      await loadDetail();
      onRefresh();
      toast.success('Factură adăugată.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva factura. Încearcă din nou.');
    }
    finally { setCreatingInvoice(false); }
  };

  const handleAddReception = async () => {
    setCreatingReception(true);
    try {
      const response: any = await createReception(contract.id, receptionForm);
      const newId = response?.reception?.id;
      if (receptionFiles.length > 0 && newId) {
        for (const file of receptionFiles) {
          await uploadReceptionFile(contract.id, newId, file);
        }
      }
      setShowReceptionModal(false);
      setReceptionForm({ reception_type: 'predare_primire', reception_date: '', reception_number: '', description: '' });
      setReceptionFiles([]);
      await loadDetail();
      onRefresh();
      toast.success('Recepție / PV adăugat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva recepția. Încearcă din nou.');
    }
    finally { setCreatingReception(false); }
  };

  const handleUploadToDoc = async (docId: number, file: File) => {
    try {
      await uploadFileToDocument(contract.id, docId, file);
      await loadDetail();
      onRefresh();
      toast.success('Fișier încărcat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut încărca fișierul. Încearcă din nou.');
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    try {
      await deleteContractDocument(contract.id, docId);
      await loadDetail();
      onRefresh();
      toast.success('Document șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge documentul. Încearcă din nou.');
    }
  };

  const handleRemoveFile = async (docId: number) => {
    try {
      const { updateContractDocument } = await import('../../../services/dossier.service');
      await updateContractDocument(contract.id, docId, { clear_file: true } as any);
      await loadDetail();
      onRefresh();
      toast.success('Fișier eliminat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut elimina fișierul. Încearcă din nou.');
    }
  };

  const handleUpdateContract = async (data: any) => {
    setSaving(true);
    try {
      await updateContract(dossierId, contract.id, data);
      setShowEdit(false);
      await loadDetail();
      onRefresh();
      toast.success('Contract salvat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva contractul. Verifică datele și încearcă din nou.');
    } finally { setSaving(false); }
  };

  const startEditDoc = (doc: any) => {
    setEditDocId(doc.id);
    setEditDocForm({ document_name: doc.document_name || '', notes: doc.notes || '', start_date: toDateStr(doc.start_date), end_date: toDateStr(doc.end_date), nr_registratura: doc.nr_registratura || '', data_registratura: toDateStr(doc.data_registratura), data_document: toDateStr(doc.data_document), data_expirare: toDateStr(doc.data_expirare) });
    setEditDocFiles([]);
  };
  const handleSaveEditDoc = async () => {
    if (!editDocId) return;
    setSavingEditDoc(true);
    try {
      await updateContractDocument(contract.id, editDocId, {
        document_name: editDocForm.document_name || undefined,
        notes: editDocForm.notes || undefined,
        start_date: editDocForm.start_date || undefined,
        end_date: editDocForm.end_date || undefined,
        nr_registratura: editDocForm.nr_registratura || undefined,
        data_registratura: editDocForm.data_registratura || undefined,
        data_document: editDocForm.data_document || undefined,
        data_expirare: editDocForm.data_expirare || undefined,
      });
      if (editDocFiles.length > 0) {
        for (const file of editDocFiles) {
          await uploadFileToDocument(contract.id, editDocId, file);
        }
      }
      setEditDocId(null); setEditDocFiles([]);
      await loadDetail(); onRefresh();
      toast.success('Document salvat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva documentul. Încearcă din nou.');
    }
    finally { setSavingEditDoc(false); }
  };

  const startEditGuarantee = (g: any) => {
    setEditGuaranteeId(g.id);
    setEditGuaranteeForm({ guarantee_type: g.guarantee_type || 'buna_executie', amount: g.amount != null ? String(g.amount) : '', issue_date: toDateStr(g.issue_date), expiry_date: toDateStr(g.expiry_date), issuer: g.issuer || '' });
  };
  const handleSaveEditGuarantee = async () => {
    if (!editGuaranteeId) return;
    setSavingEditGuarantee(true);
    try {
      await updateGuarantee(contract.id, editGuaranteeId, {
        ...editGuaranteeForm,
        amount: editGuaranteeForm.amount ? parseFloat(editGuaranteeForm.amount) : undefined,
      });
      setEditGuaranteeId(null);
      await loadDetail(); onRefresh();
      toast.success('Garanție salvată.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva garanția. Încearcă din nou.');
    }
    finally { setSavingEditGuarantee(false); }
  };

  const startEditInvoice = (inv: any) => {
    setEditInvoiceId(inv.id);
    setEditInvoiceForm({ invoice_number: inv.invoice_number || '', invoice_date: toDateStr(inv.invoice_date), amount: inv.amount != null ? String(inv.amount) : '', vat_amount: inv.vat_amount != null ? String(inv.vat_amount) : '', total_amount: inv.total_amount != null ? String(inv.total_amount) : '' });
  };
  const handleSaveEditInvoice = async () => {
    if (!editInvoiceId) return;
    setSavingEditInvoice(true);
    try {
      const amt = parseFloat(editInvoiceForm.amount) || 0;
      const vat = parseFloat(editInvoiceForm.vat_amount) || 0;
      await updateInvoice(contract.id, editInvoiceId, {
        invoice_number: editInvoiceForm.invoice_number || undefined,
        invoice_date: editInvoiceForm.invoice_date || undefined,
        amount: amt,
        vat_amount: vat,
        total_amount: editInvoiceForm.total_amount ? parseFloat(editInvoiceForm.total_amount) : amt + vat,
      });
      setEditInvoiceId(null);
      await loadDetail(); onRefresh();
      toast.success('Factură salvată.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva factura. Încearcă din nou.');
    }
    finally { setSavingEditInvoice(false); }
  };

  const startEditReception = (r: any) => {
    setEditReceptionId(r.id);
    setEditReceptionForm({ reception_type: r.reception_type || 'predare_primire', reception_date: toDateStr(r.reception_date), reception_number: r.reception_number || '', description: r.description || '' });
  };
  const handleSaveEditReception = async () => {
    if (!editReceptionId) return;
    setSavingEditReception(true);
    try {
      await updateReception(contract.id, editReceptionId, editReceptionForm);
      setEditReceptionId(null);
      await loadDetail(); onRefresh();
      toast.success('Recepție / PV salvat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva recepția. Încearcă din nou.');
    }
    finally { setSavingEditReception(false); }
  };

  const handleDeleteFile = async (fileId: number | string) => {
    if (typeof fileId === 'string') return; // legacy file, skip
    try {
      await deleteContractFile(contract.id, fileId);
      await loadDetail();
      onRefresh();
      toast.success('Fișier șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge fișierul. Încearcă din nou.');
    }
  };

  const handleDeleteGuarantee = async (id: number) => {
    try {
      await deleteGuarantee(contract.id, id);
      await loadDetail(); onRefresh();
      toast.success('Garanție ștearsă.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge garanția. Încearcă din nou.');
    }
  };

  const handleDeleteInvoice = async (id: number) => {
    try {
      await deleteInvoice(contract.id, id);
      await loadDetail(); onRefresh();
      toast.success('Factură ștearsă.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge factura. Încearcă din nou.');
    }
  };

  const handleDeleteReception = async (id: number) => {
    try {
      await deleteReception(contract.id, id);
      await loadDetail(); onRefresh();
      toast.success('Recepție / PV șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge recepția. Încearcă din nou.');
    }
  };

  // Render files list for an entity (uses .files array from detail)
  // docTitle: denumirea entității, folosită ca nume implicit al activității corelate.
  const renderFiles = (item: any, entityType: string, entityId: number, docTitle?: string) => {
    const files = item.files || [];
    return (
      <div className="mt-1 space-y-1">
        {files.map((f: any) => (
          <div key={f.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1.5">
            <FileUp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="flex-1 truncate text-gray-600 text-xs">{f.file_name}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">{f.file_size ? `${(f.file_size / 1024).toFixed(0)} KB` : ''}</span>
            {f.file_name?.toLowerCase().endsWith('.pdf') && (
              <button onClick={() => window.open(`/auth/files/${f.attachment_id}/${encodeURIComponent(f.file_name)}?inline=1`, '_blank')}
                className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Vizualizează">
                <Eye className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => downloadAttachmentFile(f.attachment_id, f.file_name)}
              className="p-1 text-gray-400 hover:text-green-600 rounded hover:bg-green-100 transition-colors" title="Descarcă">
              <Download className="w-3.5 h-3.5" />
            </button>
            {f.attachment_id && projectId ? (
              <AddActivityForDocumentButton
                projectId={projectId}
                attachmentId={f.attachment_id}
                fileName={f.file_name}
                docTitle={docTitle}
                variant="icon"
                size="sm"
                onCreated={loadDetail}
              />
            ) : null}
            <button onClick={() => setPendingDelete({ action: () => handleDeleteFile(f.id), message: `Ești sigur că vrei să ștergi fișierul "${f.file_name}"?` })}
              className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge fișier">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <label className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded border border-dashed border-gray-300 cursor-pointer hover:border-primary-400 transition-colors text-xs text-gray-500">
          <Plus className="w-3.5 h-3.5" />
          <span>Adaugă fișier</span>
          <input type="file" className="hidden" onChange={async (e) => {
            if (!e.target.files?.[0]) return;
            const file = e.target.files[0];
            e.target.value = '';
            const uploadFn = entityType === 'guarantee' ? uploadGuaranteeFile
              : entityType === 'invoice' ? uploadInvoiceFile
              : entityType === 'reception' ? uploadReceptionFile
              : (cId: number, eId: number, f: File) => uploadFileToDocument(cId, eId, f);
            try {
              await uploadFn(contract.id, entityId, file);
              await loadDetail();
              onRefresh();
              toast.success('Fișier încărcat.');
            } catch (err) {
              console.error(err);
              toast.error('Nu am putut încărca fișierul. Încearcă din nou.');
            }
          }} />
        </label>
      </div>
    );
  };

  const openAddDocModal = (category: 'contract' | 'aditional') => {
    setDocCategory(category);
    setShowDocModal(true);
    setDocForm({ document_name: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
    setDocFiles([]);
  };

  // Randează o secțiune pliabilă de documente (reutilizează renderFiles / modalul existent).
  const renderDocSection = (title: string, sectionKey: string, docs: any[], category: 'contract' | 'aditional', emptyLabel: string) => (
    <div className="border rounded-lg overflow-hidden">
      <button onClick={() => setActiveSection(activeSection === sectionKey ? null : sectionKey)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white">
        <span className="text-gray-600">{title} ({docs.length})</span>
        <button onClick={e => { e.stopPropagation(); openAddDocModal(category); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
      </button>
      {activeSection === sectionKey && (
        <div className="px-3 pb-2 border-t">
          {docs.length === 0 ? (
            <div className="flex flex-col items-start gap-2 py-3">
              <p className="text-xs text-gray-400">{emptyLabel}</p>
              <button onClick={() => openAddDocModal(category)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                <Plus className="w-4 h-4" />
                Adaugă document
              </button>
            </div>
          ) : (
            <div className="space-y-1 mt-1">
              {docs.map((doc: any) => (
                <div key={doc.id} className="py-2 px-2 rounded hover:bg-white group border-b last:border-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 truncate">{doc.document_name || doc.file_name || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        {(doc.start_date || doc.end_date) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-100" title="Perioadă">
                            {doc.start_date ? formatDateDisplay(toDateStr(doc.start_date)) : '—'} → {doc.end_date ? formatDateDisplay(toDateStr(doc.end_date)) : '—'}
                          </span>
                        )}
                        {doc.notes && <span className="italic truncate max-w-[200px]">{doc.notes}</span>}
                      </div>
                      {(doc.nr_registratura || doc.data_registratura || doc.data_document || doc.data_expirare) && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {doc.nr_registratura && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Nr. registratură">
                              Nr. {doc.nr_registratura}{doc.data_registratura ? ` / ${formatDateDisplay(toDateStr(doc.data_registratura))}` : ''}
                            </span>
                          )}
                          {!doc.nr_registratura && doc.data_registratura && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Data registratură">
                              Reg. {formatDateDisplay(toDateStr(doc.data_registratura))}
                            </span>
                          )}
                          {doc.data_document && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200" title="Data document">
                              Doc. {formatDateDisplay(toDateStr(doc.data_document))}
                            </span>
                          )}
                          {doc.data_expirare && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-100" title="Data expirare">
                              Exp. {formatDateDisplay(toDateStr(doc.data_expirare))}
                            </span>
                          )}
                        </div>
                      )}
                      {renderFiles(doc, 'document', doc.id, doc.document_name || doc.file_name || undefined)}
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button onClick={() => startEditDoc(doc)} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-100 transition-colors" title="Editează"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setPendingDelete({ action: () => handleDeleteDocument(doc.id), message: `Ești sigur că vrei să ștergi documentul "${doc.document_name || doc.file_name || 'Document'}"?` })} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="Sterge document"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {/* Adăugare documente suplimentare — vizibil și când există deja documente. */}
              <button onClick={() => openAddDocModal(category)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                <Plus className="w-4 h-4" />
                Adaugă document
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white">
      <div className="flex items-center hover:bg-blue-50 transition-colors cursor-pointer group">
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex-1 flex items-center gap-3 px-4 py-3 text-left">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
          <StatusDot color={contract.status === 'active' ? 'green' : contract.status === 'completed' ? 'green' : 'gray'} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{contract.contract_name}</div>
            <div className="text-xs text-gray-400 truncate">{contractTypeLabels[contract.contract_type] || contract.contract_type} | {contract.contract_number || 'Fara numar'}</div>
            {contract.procurement_name ? (
              <div className="text-xs text-blue-600 truncate" title={`Provine din achiziția: ${contract.procurement_name}`}>Provine din achiziția: {contract.procurement_name}</div>
            ) : (
              <div className="text-xs text-amber-600 truncate" title="Contract fără achiziție de proveniență">Contract fără achiziție de proveniență</div>
            )}
            {contract.responsible_name ? (
              <div className="text-xs text-gray-500 truncate" title={`Responsabil: ${contract.responsible_name}`}>Responsabil: {contract.responsible_name}</div>
            ) : (
              <div className="text-xs text-gray-400 truncate">Fără responsabil</div>
            )}
          </div>
          <MoneyDisplay value={remaining} size="sm" color={remaining < 0 ? 'red' : 'default'} />
        </button>
        <div className="flex items-center gap-1 pr-4">
          <button onClick={(e) => { e.stopPropagation(); setShowEdit(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setShowDelete(true); }} className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Edit Contract Modal */}
      <Modal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        title="Editare contract"
        size="xl"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setShowEdit(false)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={() => editFormRef.current?.submit()} disabled={saving} isLoading={saving}>
              {saving ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <ContractForm
          ref={editFormRef}
          dossierId={dossierId}
          projectId={projectId}
          initialData={contract}
          onSubmit={handleUpdateContract}
          onCancel={() => setShowEdit(false)}
          bare
        />
      </Modal>

      {/* Add Document Modal */}
      <Modal
        isOpen={showDocModal}
        onClose={() => { setShowDocModal(false); setDocFiles([]); }}
        title="Adaugă document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowDocModal(false); setDocFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddDocument} disabled={creatingDoc || !docForm.document_name} isLoading={creatingDoc}>
              {creatingDoc ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Denumire document *</label>
            <input placeholder="Denumire document" value={docForm.document_name} onChange={e => setDocForm(f => ({...f, document_name: e.target.value}))} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data începere" tooltip="Perioada de valabilitate — începutul" value={docForm.start_date} onChange={e => setDocForm(f => ({...f, start_date: e.target.value}))} />
            <DateInput label="Data finalizare" tooltip="Perioada de valabilitate — sfârșitul" value={docForm.end_date} onChange={e => setDocForm(f => ({...f, end_date: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea placeholder="Observații" value={docForm.notes} onChange={e => setDocForm(f => ({...f, notes: e.target.value}))} rows={2} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <span>Nr. registratură</span>
                <span title="Numărul unic atribuit de registratura instituției" className="cursor-help text-gray-400 hover:text-gray-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </label>
              <input value={docForm.nr_registratura} onChange={e => setDocForm(f => ({...f, nr_registratura: e.target.value}))} placeholder="Nr. registratură" className={inputClass} />
            </div>
            <DateInput label="Data registratură" tooltip="Data înregistrării la registratura instituției" value={docForm.data_registratura} onChange={e => setDocForm(f => ({...f, data_registratura: e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" tooltip="Data emiterii/semnării documentului" value={docForm.data_document} onChange={e => setDocForm(f => ({...f, data_document: e.target.value}))} />
            <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil" value={docForm.data_expirare} onChange={e => setDocForm(f => ({...f, data_expirare: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fișiere</label>
            <FileDropArea files={docFiles} onFilesChange={setDocFiles} id={`add-contract-doc-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Add Guarantee Modal */}
      <Modal
        isOpen={showGuaranteeModal}
        onClose={() => { setShowGuaranteeModal(false); setGuaranteeFiles([]); }}
        title="Adaugă garanție"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowGuaranteeModal(false); setGuaranteeFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddGuarantee} disabled={creatingGuarantee} isLoading={creatingGuarantee}>
              {creatingGuarantee ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip garanție</label>
            <SearchableSelect
              value={guaranteeForm.guarantee_type}
              onChange={v => setGuaranteeForm(f => ({...f, guarantee_type: v as string}))}
              options={[
                { value: 'buna_executie', label: 'Bună execuție' },
                { value: 'participare', label: 'Participare' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emitent</label>
            <input placeholder="Emitent" value={guaranteeForm.issuer} onChange={e => setGuaranteeForm(f => ({...f, issuer: e.target.value}))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valoare (RON)</label>
            <input type="number" placeholder="Valoare (RON)" value={guaranteeForm.amount} onChange={e => setGuaranteeForm(f => ({...f, amount: e.target.value}))} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data emitere" value={guaranteeForm.issue_date} onChange={e => setGuaranteeForm(f => ({...f, issue_date: e.target.value}))} />
            <DateInput label="Data expirare" value={guaranteeForm.expiry_date} onChange={e => setGuaranteeForm(f => ({...f, expiry_date: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document garanție</label>
            <FileDropArea files={guaranteeFiles} onFilesChange={setGuaranteeFiles} id={`add-guarantee-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Add Invoice Modal */}
      <Modal
        isOpen={showInvoiceModal}
        onClose={() => { setShowInvoiceModal(false); setInvoiceFiles([]); }}
        title="Adaugă factură"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowInvoiceModal(false); setInvoiceFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddInvoice} disabled={creatingInvoice} isLoading={creatingInvoice}>
              {creatingInvoice ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr. factură</label>
              <input placeholder="Nr. factură" value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(f => ({...f, invoice_number: e.target.value}))} className={inputClass} />
            </div>
            <DateInput label="Data facturii" value={invoiceForm.invoice_date} onChange={e => setInvoiceForm(f => ({...f, invoice_date: e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valoare (RON)</label>
              <input type="number" placeholder="Valoare fără TVA" value={invoiceForm.amount} onChange={e => setInvoiceForm(f => ({...f, amount: e.target.value}))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TVA (RON)</label>
              <input type="number" placeholder="TVA" value={invoiceForm.vat_amount} onChange={e => setInvoiceForm(f => ({...f, vat_amount: e.target.value}))} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document factură</label>
            <FileDropArea files={invoiceFiles} onFilesChange={setInvoiceFiles} id={`add-invoice-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Add Reception Modal */}
      <Modal
        isOpen={showReceptionModal}
        onClose={() => { setShowReceptionModal(false); setReceptionFiles([]); }}
        title="Adaugă recepție / PV"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowReceptionModal(false); setReceptionFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddReception} disabled={creatingReception} isLoading={creatingReception}>
              {creatingReception ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip recepție</label>
            <SearchableSelect
              value={receptionForm.reception_type}
              onChange={v => setReceptionForm(f => ({...f, reception_type: v as string}))}
              options={[
                { value: 'predare_primire', label: 'Predare-primire' },
                { value: 'terminare_lucrari', label: 'Terminare lucrări' },
                { value: 'receptie_finala', label: 'Recepție finală' },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data recepției" value={receptionForm.reception_date} onChange={e => setReceptionForm(f => ({...f, reception_date: e.target.value}))} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Număr PV</label>
              <input placeholder="Număr PV" value={receptionForm.reception_number} onChange={e => setReceptionForm(f => ({...f, reception_number: e.target.value}))} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
            <textarea placeholder="Descriere" value={receptionForm.description} onChange={e => setReceptionForm(f => ({...f, description: e.target.value}))} rows={2} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document PV</label>
            <FileDropArea files={receptionFiles} onFilesChange={setReceptionFiles} id={`add-reception-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Edit Document Modal */}
      <Modal
        isOpen={editDocId !== null}
        onClose={() => { setEditDocId(null); setEditDocFiles([]); }}
        title="Editare document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setEditDocId(null); setEditDocFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEditDoc} disabled={savingEditDoc} isLoading={savingEditDoc}>
              {savingEditDoc ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Denumire document *</label>
            <input placeholder="Denumire document" value={editDocForm.document_name} onChange={e => setEditDocForm(f => ({...f, document_name: e.target.value}))} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data începere" tooltip="Perioada de valabilitate — începutul" value={editDocForm.start_date} onChange={e => setEditDocForm(f => ({...f, start_date: e.target.value}))} />
            <DateInput label="Data finalizare" tooltip="Perioada de valabilitate — sfârșitul" value={editDocForm.end_date} onChange={e => setEditDocForm(f => ({...f, end_date: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea placeholder="Observații" value={editDocForm.notes} onChange={e => setEditDocForm(f => ({...f, notes: e.target.value}))} rows={2} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <span>Nr. registratură</span>
                <span title="Numărul unic atribuit de registratura instituției" className="cursor-help text-gray-400 hover:text-gray-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </label>
              <input value={editDocForm.nr_registratura} onChange={e => setEditDocForm(f => ({...f, nr_registratura: e.target.value}))} placeholder="Nr. registratură" className={inputClass} />
            </div>
            <DateInput label="Data registratură" tooltip="Data înregistrării la registratura instituției" value={editDocForm.data_registratura} onChange={e => setEditDocForm(f => ({...f, data_registratura: e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" tooltip="Data emiterii/semnării documentului" value={editDocForm.data_document} onChange={e => setEditDocForm(f => ({...f, data_document: e.target.value}))} />
            <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil" value={editDocForm.data_expirare} onChange={e => setEditDocForm(f => ({...f, data_expirare: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adaugă fișiere noi</label>
            <FileDropArea files={editDocFiles} onFilesChange={setEditDocFiles} id={`edit-contract-doc-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Edit Guarantee Modal */}
      <Modal
        isOpen={editGuaranteeId !== null}
        onClose={() => setEditGuaranteeId(null)}
        title="Editare garanție"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditGuaranteeId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEditGuarantee} disabled={savingEditGuarantee} isLoading={savingEditGuarantee}>
              {savingEditGuarantee ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip garanție</label>
            <SearchableSelect
              value={editGuaranteeForm.guarantee_type}
              onChange={v => setEditGuaranteeForm(f => ({...f, guarantee_type: v as string}))}
              options={[
                { value: 'buna_executie', label: 'Bună execuție' },
                { value: 'participare', label: 'Participare' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emitent</label>
            <input placeholder="Emitent" value={editGuaranteeForm.issuer} onChange={e => setEditGuaranteeForm(f => ({...f, issuer: e.target.value}))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valoare (RON)</label>
            <input type="number" placeholder="Valoare (RON)" value={editGuaranteeForm.amount} onChange={e => setEditGuaranteeForm(f => ({...f, amount: e.target.value}))} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data emitere" value={editGuaranteeForm.issue_date} onChange={e => setEditGuaranteeForm(f => ({...f, issue_date: e.target.value}))} />
            <DateInput label="Data expirare" value={editGuaranteeForm.expiry_date} onChange={e => setEditGuaranteeForm(f => ({...f, expiry_date: e.target.value}))} />
          </div>
        </div>
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        isOpen={editInvoiceId !== null}
        onClose={() => setEditInvoiceId(null)}
        title="Editare factură"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditInvoiceId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEditInvoice} disabled={savingEditInvoice} isLoading={savingEditInvoice}>
              {savingEditInvoice ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr. factură</label>
              <input placeholder="Nr. factură" value={editInvoiceForm.invoice_number} onChange={e => setEditInvoiceForm(f => ({...f, invoice_number: e.target.value}))} className={inputClass} />
            </div>
            <DateInput label="Data facturii" value={editInvoiceForm.invoice_date} onChange={e => setEditInvoiceForm(f => ({...f, invoice_date: e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valoare (RON)</label>
              <input type="number" placeholder="Valoare fără TVA" value={editInvoiceForm.amount} onChange={e => setEditInvoiceForm(f => ({...f, amount: e.target.value}))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TVA (RON)</label>
              <input type="number" placeholder="TVA" value={editInvoiceForm.vat_amount} onChange={e => setEditInvoiceForm(f => ({...f, vat_amount: e.target.value}))} className={inputClass} />
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Reception Modal */}
      <Modal
        isOpen={editReceptionId !== null}
        onClose={() => setEditReceptionId(null)}
        title="Editare recepție / PV"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditReceptionId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEditReception} disabled={savingEditReception} isLoading={savingEditReception}>
              {savingEditReception ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip recepție</label>
            <SearchableSelect
              value={editReceptionForm.reception_type}
              onChange={v => setEditReceptionForm(f => ({...f, reception_type: v as string}))}
              options={[
                { value: 'predare_primire', label: 'Predare-primire' },
                { value: 'terminare_lucrari', label: 'Terminare lucrări' },
                { value: 'receptie_finala', label: 'Recepție finală' },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data recepției" value={editReceptionForm.reception_date} onChange={e => setEditReceptionForm(f => ({...f, reception_date: e.target.value}))} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Număr PV</label>
              <input placeholder="Număr PV" value={editReceptionForm.reception_number} onChange={e => setEditReceptionForm(f => ({...f, reception_number: e.target.value}))} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
            <textarea placeholder="Descriere" value={editReceptionForm.description} onChange={e => setEditReceptionForm(f => ({...f, description: e.target.value}))} rows={2} className={inputClass} />
          </div>
        </div>
      </Modal>

      {/* Add Addendum Modal */}
      <Modal
        isOpen={showAddendumModal}
        onClose={() => { setShowAddendumModal(false); setAddendumFiles([]); }}
        title="Adaugă act adițional"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowAddendumModal(false); setAddendumFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddAddendum} disabled={creatingAddendum} isLoading={creatingAddendum}>
              {creatingAddendum ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Număr act</label>
              <input placeholder="Număr act (opțional)" value={addendumForm.addendum_number} onChange={e => setAddendumForm(f => ({...f, addendum_number: e.target.value}))} className={inputClass} />
            </div>
            <DateInput label="Data actului" value={addendumForm.addendum_date} onChange={e => setAddendumForm(f => ({...f, addendum_date: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Efect asupra valorii contractului</label>
            <SearchableSelect
              value={addendumForm.amount_mode === 'none' ? 'none' : 'absolute'}
              onChange={v => setAddendumForm(f => ({...f, amount_mode: v as 'absolute' | 'none'}))}
              options={[
                { value: 'absolute', label: 'Stabilește noua valoare a contractului' },
                { value: 'none', label: 'Fără modificare de valoare (doar durată/clauze)' },
              ]}
            />
          </div>
          {addendumForm.amount_mode !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Noua valoare a contractului (RON)</label>
              <input type="number" placeholder="Valoarea totală a contractului conform actului adițional" value={addendumForm.value_change} onChange={e => setAddendumForm(f => ({...f, value_change: e.target.value}))} className={inputClass} />
              <p className="mt-1 text-xs text-gray-500">Aceasta devine valoarea curentă a contractului. Cel mai recent act adițional determină valoarea totală.</p>
            </div>
          )}
          <DateInput label="Data nouă de finalizare" tooltip="Noua dată de finalizare a contractului (opțional)" value={addendumForm.new_end_date} onChange={e => setAddendumForm(f => ({...f, new_end_date: e.target.value}))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
            <textarea placeholder="Descriere" value={addendumForm.description} onChange={e => setAddendumForm(f => ({...f, description: e.target.value}))} rows={2} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document act adițional (PDF)</label>
            <FileDropArea files={addendumFiles} onFilesChange={setAddendumFiles} id={`add-addendum-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {/* Edit Addendum Modal */}
      <Modal
        isOpen={editAddendumId !== null}
        onClose={() => { setEditAddendumId(null); setEditAddendumFiles([]); }}
        title="Editare act adițional"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setEditAddendumId(null); setEditAddendumFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEditAddendum} disabled={savingEditAddendum} isLoading={savingEditAddendum}>
              {savingEditAddendum ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Număr act</label>
              <input placeholder="Număr act (opțional)" value={editAddendumForm.addendum_number} onChange={e => setEditAddendumForm(f => ({...f, addendum_number: e.target.value}))} className={inputClass} />
            </div>
            <DateInput label="Data actului" value={editAddendumForm.addendum_date} onChange={e => setEditAddendumForm(f => ({...f, addendum_date: e.target.value}))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Efect asupra valorii contractului</label>
            <SearchableSelect
              value={editAddendumForm.amount_mode}
              onChange={v => setEditAddendumForm(f => ({...f, amount_mode: v as 'delta' | 'absolute' | 'none'}))}
              options={[
                { value: 'absolute', label: 'Stabilește noua valoare a contractului' },
                { value: 'none', label: 'Fără modificare de valoare (doar durată/clauze)' },
                // Opțiune păstrată doar pentru actele istorice introduse ca diferență (+/-)
                ...(editAddendumForm.amount_mode === 'delta' ? [{ value: 'delta', label: 'Modificare (+/-) — act istoric' }] : []),
              ]}
            />
          </div>
          {editAddendumForm.amount_mode !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{editAddendumForm.amount_mode === 'delta' ? 'Modificare valoare (RON)' : 'Noua valoare a contractului (RON)'}</label>
              <input type="number" placeholder={editAddendumForm.amount_mode === 'delta' ? 'Ex: +10000 sau -5000' : 'Valoarea totală a contractului conform actului adițional'} value={editAddendumForm.value_change} onChange={e => setEditAddendumForm(f => ({...f, value_change: e.target.value}))} className={inputClass} />
            </div>
          )}
          <DateInput label="Data nouă de finalizare" tooltip="Noua dată de finalizare a contractului (opțional)" value={editAddendumForm.new_end_date} onChange={e => setEditAddendumForm(f => ({...f, new_end_date: e.target.value}))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
            <textarea placeholder="Descriere" value={editAddendumForm.description} onChange={e => setEditAddendumForm(f => ({...f, description: e.target.value}))} rows={2} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Înlocuiește document (PDF)</label>
            <FileDropArea files={editAddendumFiles} onFilesChange={setEditAddendumFiles} id={`edit-addendum-${contract.id}`} compact />
          </div>
        </div>
      </Modal>

      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-gray-50">
          {/* Financial Summary */}
          <div className="my-3 p-3 bg-white rounded-lg border text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Valoare inițială:</span> <MoneyDisplay value={parseFloat(contract.contract_value) || 0} size="sm" /></div>
            {addendumTotal !== 0 && (
              <div className="flex justify-between"><span className="text-gray-500">+ Acte adiționale (net):</span> <MoneyDisplay value={addendumTotal} size="sm" color={addendumTotal > 0 ? 'default' : 'red'} /></div>
            )}
            <div className="flex justify-between border-t pt-1"><span className="text-gray-500 font-medium">Valoare curentă:</span> <MoneyDisplay value={adjusted} size="sm" /></div>
            <div className="flex justify-between"><span className="text-gray-500">- Facturat:</span> <MoneyDisplay value={-invoiced} size="sm" /></div>
            <div className="flex justify-between border-t pt-1 font-semibold">
              <span className="text-gray-800">REST DE PLATA:</span>
              <MoneyDisplay value={remaining} size="lg" color={remaining < 0 ? 'red' : 'green'} />
            </div>
            {detail && ((detail as any).deliverables_count != null || (detail as any).events_count != null) && (
              <div className="flex justify-between border-t pt-1 text-xs text-gray-500">
                <span>Conține:</span>
                <span>Livrabile: {Number((detail as any).deliverables_count) || 0} · Evenimente implementare: {Number((detail as any).events_count) || 0}</span>
              </div>
            )}
          </div>

          {/* Sub-sections */}
          {detail && (
            <div className="space-y-2">
              {/* Documente — împărțite în „contract" și „adiționale" pe baza document_type */}
              {(() => {
                const allDocs = detail.documents || [];
                const docsContract = allDocs.filter((d: any) => d.document_type === 'contract');
                const docsAditionale = allDocs.filter((d: any) => d.document_type !== 'contract');
                return (
                  <>
                    {renderDocSection('Documente contract', 'docs_contract', docsContract, 'contract', 'Niciun document')}
                    {renderDocSection('Documente adiționale', 'docs_aditional', docsAditionale, 'aditional', 'Niciun document adițional')}
                  </>
                );
              })()}

              {/* Acte adiționale — entitatea care modifică valoarea/durata contractului */}
              <div className="border rounded-lg overflow-hidden">
                <button onClick={() => setActiveSection(activeSection === 'addendums' ? null : 'addendums')} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white">
                  <span className="text-gray-600">Acte adiționale ({(detail.addendums || []).length})</span>
                  <button onClick={e => { e.stopPropagation(); setAddendumForm({ ...emptyAddendum }); setAddendumFiles([]); setShowAddendumModal(true); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
                </button>
                {activeSection === 'addendums' && (
                  <div className="px-3 pb-2 border-t">
                    {(detail.addendums || []).length === 0 ? (
                      <div className="flex flex-col items-start gap-2 py-3">
                        <p className="text-xs text-gray-400">Niciun act adițional</p>
                        <button onClick={() => { setAddendumForm({ ...emptyAddendum }); setAddendumFiles([]); setShowAddendumModal(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                          <Plus className="w-4 h-4" />
                          Adaugă act adițional
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1 mt-1">
                        {(detail.addendums || []).map((a: any) => {
                          const vc = parseFloat(a.value_change) || 0;
                          return (
                            <div key={a.id} className="py-2 px-2 rounded hover:bg-white group border-b last:border-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700">Act adițional{a.addendum_number ? ` nr. ${a.addendum_number}` : ''}</span>
                                    {a.addendum_date && <span className="text-xs text-gray-400">{formatDateDisplay(toDateStr(a.addendum_date))}</span>}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                                    {a.amount_mode === 'none' ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">fără efect asupra valorii</span>
                                    ) : a.amount_mode === 'absolute' ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100">
                                        = <MoneyDisplay value={vc} size="sm" /> (valoare nouă)
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-50 border border-green-100" title="Modificare valoare">
                                        <MoneyDisplay value={vc} size="sm" color={vc < 0 ? 'red' : 'default'} />
                                      </span>
                                    )}
                                    {a.new_end_date && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-100" title="Noua dată de finalizare">
                                        Nouă finalizare: {formatDateDisplay(toDateStr(a.new_end_date))}
                                      </span>
                                    )}
                                  </div>
                                  {a.description && <div className="mt-0.5 text-xs text-gray-500 italic truncate">{a.description}</div>}
                                  {a.attachment_id && a.file_name && (
                                    <div className="mt-1 flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1.5">
                                      <FileUp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                      <span className="flex-1 truncate text-gray-600 text-xs">{a.file_name}</span>
                                      {a.file_size ? <span className="text-xs text-gray-400 flex-shrink-0">{(a.file_size / 1024).toFixed(0)} KB</span> : null}
                                      {a.file_name?.toLowerCase().endsWith('.pdf') && (
                                        <button onClick={() => window.open(`/auth/files/${a.attachment_id}/${encodeURIComponent(a.file_name)}?inline=1`, '_blank')}
                                          className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Vizualizează">
                                          <Eye className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      <button onClick={() => downloadAttachmentFile(a.attachment_id, a.file_name)}
                                        className="p-1 text-gray-400 hover:text-green-600 rounded hover:bg-green-100 transition-colors" title="Descarcă">
                                        <Download className="w-3.5 h-3.5" />
                                      </button>
                                      {projectId ? (
                                        <AddActivityForDocumentButton
                                          projectId={projectId}
                                          attachmentId={a.attachment_id}
                                          fileName={a.file_name}
                                          docTitle={`Act adițional${a.addendum_number ? ` nr. ${a.addendum_number}` : ''}`}
                                          variant="icon"
                                          size="sm"
                                          onCreated={loadDetail}
                                        />
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                  <button onClick={() => startEditAddendum(a)} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-100 transition-colors" title="Editează"><Pencil className="w-4 h-4" /></button>
                                  <button onClick={() => setPendingDelete({ action: () => handleDeleteAddendum(a.id), message: `Ești sigur că vrei să ștergi acest act adițional?` })} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={() => { setAddendumForm({ ...emptyAddendum }); setAddendumFiles([]); setShowAddendumModal(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                          <Plus className="w-4 h-4" />
                          Adaugă act adițional
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Guarantees */}
              <div className="border rounded-lg overflow-hidden">
                <button onClick={() => setActiveSection(activeSection === 'guarantees' ? null : 'guarantees')} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white">
                  <span className="text-gray-600">Garanții ({detail.guarantees.length})</span>
                  <button onClick={e => { e.stopPropagation(); setShowGuaranteeModal(true); setGuaranteeForm({ guarantee_type: 'buna_executie', amount: '', issue_date: '', expiry_date: '', issuer: '' }); setGuaranteeFiles([]); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
                </button>
                {activeSection === 'guarantees' && (
                  <div className="px-3 pb-2 border-t">
                    {detail.guarantees.length === 0 ? (
                      <div className="flex flex-col items-start gap-2 py-3">
                        <p className="text-xs text-gray-400">Nicio garanție</p>
                        <button onClick={() => { setShowGuaranteeModal(true); setGuaranteeForm({ guarantee_type: 'buna_executie', amount: '', issue_date: '', expiry_date: '', issuer: '' }); setGuaranteeFiles([]); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                          <Plus className="w-4 h-4" />
                          Adaugă garanție
                        </button>
                      </div>
                    ) : (
                      detail.guarantees.map(g => (
                        <div key={g.id} className="py-1.5 text-sm border-b last:border-0 group">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">{g.guarantee_type === 'buna_executie' ? 'Bună execuție' : 'Participare'} — {g.issuer || 'N/A'}</span>
                            <div className="flex items-center gap-2">
                              <MoneyDisplay value={g.amount || 0} size="sm" />
                              {g.expiry_date && <span className="text-xs text-gray-400">exp: {formatDateDisplay(toDateStr(g.expiry_date))}</span>}
                              <button onClick={() => startEditGuarantee(g)} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-100 transition-colors" title="Editează"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => setPendingDelete({ action: () => { handleDeleteGuarantee(g.id); }, message: `Ești sigur că vrei să ștergi această garanție?` })} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          {renderFiles(g, 'guarantee', g.id, `Garanție ${g.guarantee_type === 'buna_executie' ? 'bună execuție' : 'participare'}${g.issuer ? ` — ${g.issuer}` : ''}`)}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Invoices */}
              <div className="border rounded-lg overflow-hidden">
                <button onClick={() => setActiveSection(activeSection === 'invoices' ? null : 'invoices')} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white">
                  <span className="text-gray-600">Facturi ({detail.invoices.length})</span>
                  <button onClick={e => { e.stopPropagation(); setShowInvoiceModal(true); setInvoiceForm({ invoice_number: '', invoice_date: '', amount: '', vat_amount: '', total_amount: '' }); setInvoiceFiles([]); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
                </button>
                {activeSection === 'invoices' && (
                  <div className="px-3 pb-2 border-t">
                    {detail.invoices.length === 0 ? (
                      <div className="flex flex-col items-start gap-2 py-3">
                        <p className="text-xs text-gray-400">Nicio factură</p>
                        <button onClick={() => { setShowInvoiceModal(true); setInvoiceForm({ invoice_number: '', invoice_date: '', amount: '', vat_amount: '', total_amount: '' }); setInvoiceFiles([]); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                          <Plus className="w-4 h-4" />
                          Adaugă factură
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1 mt-1">
                        {detail.invoices.map(inv => (
                          <div key={inv.id} className="py-1.5 text-sm border-b last:border-0 group">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-gray-700 font-medium">{inv.invoice_number || 'Fără nr.'}</span>
                                <span className="text-xs text-gray-400">{formatDateDisplay(toDateStr(inv.invoice_date))}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <MoneyDisplay value={inv.total_amount} size="sm" />
                                <span className="text-xs text-gray-400">rest: </span>
                                <MoneyDisplay value={inv.remaining_value || 0} size="sm" color={(inv.remaining_value || 0) < 0 ? 'red' : 'default'} />
                                <button onClick={() => startEditInvoice(inv)} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-100 transition-colors" title="Editează"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => setPendingDelete({ action: () => { handleDeleteInvoice(inv.id); }, message: `Ești sigur că vrei să ștergi factura "${inv.invoice_number}"?` })} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </div>
                            {renderFiles(inv, 'invoice', inv.id, `Factură ${inv.invoice_number || 'fără nr.'}`)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Receptions */}
              <div className="border rounded-lg overflow-hidden">
                <button onClick={() => setActiveSection(activeSection === 'receptions' ? null : 'receptions')} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white">
                  <span className="text-gray-600">Recepții / PV ({(detail.receptions || []).length})</span>
                  <button onClick={e => { e.stopPropagation(); setShowReceptionModal(true); setReceptionForm({ reception_type: 'predare_primire', reception_date: '', reception_number: '', description: '' }); setReceptionFiles([]); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
                </button>
                {activeSection === 'receptions' && (
                  <div className="px-3 pb-2 border-t">
                    {(detail.receptions || []).length === 0 ? (
                      <div className="flex flex-col items-start gap-2 py-3">
                        <p className="text-xs text-gray-400">Niciun PV de recepție</p>
                        <button onClick={() => { setShowReceptionModal(true); setReceptionForm({ reception_type: 'predare_primire', reception_date: '', reception_number: '', description: '' }); setReceptionFiles([]); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors">
                          <Plus className="w-4 h-4" />
                          Adaugă recepție / PV
                        </button>
                      </div>
                    ) : (
                      (detail.receptions || []).map((r: any) => (
                        <div key={r.id} className="py-1.5 text-sm border-b last:border-0 group">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">{r.reception_type === 'predare_primire' ? 'PV Predare-primire' : r.reception_type === 'terminare_lucrari' ? 'PV Terminare lucrări' : 'PV Recepție finală'} {r.reception_number ? `nr. ${r.reception_number}` : ''}</span>
                            <div className="flex items-center gap-2">
                              {r.reception_date && <span className="text-xs text-gray-400">{formatDateDisplay(toDateStr(r.reception_date))}</span>}
                              <button onClick={() => startEditReception(r)} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-100 transition-colors" title="Editează"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => setPendingDelete({ action: () => { handleDeleteReception(r.id); }, message: `Ești sigur că vrei să ștergi acest PV de recepție?` })} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          {renderFiles(r, 'reception', r.id, `${r.reception_type === 'predare_primire' ? 'PV Predare-primire' : r.reception_type === 'terminare_lucrari' ? 'PV Terminare lucrări' : 'PV Recepție finală'}${r.reception_number ? ` nr. ${r.reception_number}` : ''}`)}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDelete isOpen={showDelete} onClose={() => setShowDelete(false)} onConfirm={onDelete}
        message="Ești sigur că vrei să ștergi acest contract? Acțiunea nu poate fi anulată." />
      <ConfirmDelete
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => { if (pendingDelete) pendingDelete.action(); }}
        message={pendingDelete?.message}
      />
    </div>
  );
}
